import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Check, Loader2 } from 'lucide-react';
import apiService from '../services/api';
import { useAuthBranding } from '../hooks/useAuthBranding';
import { usePasswordPolicy, evaluatePassword } from '../hooks/usePasswordPolicy';
import PasswordRequirements from './common/PasswordRequirements';

const AcceptInvite: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteData, setInviteData] = useState<any>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [accepting, setAccepting] = useState(false);

  // Focus states
  const [nameFocused, setNameFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [confirmFocused, setConfirmFocused] = useState(false);

  // Hover state
  const [submitHovered, setSubmitHovered] = useState(false);
const { branding } = useAuthBranding('Accept Invite');

// Password validation — driven by the server policy. Breach and name/email
  // screening are server-side only, so allRequirementsMet does not guarantee
  // the server will accept the password.
  const { policy: passwordPolicy } = usePasswordPolicy();
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const { meetsClientChecks: allRequirementsMet } = evaluatePassword(
    password,
    passwordPolicy
  );

  useEffect(() => {
    loadInvite();
  }, [token]);


  const loadInvite = async () => {
    try {
      const response = await apiService.get(`/auth/invite/${token}`);
      setInviteData(response.data);
      setLoading(false);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Invalid or expired invitation');
      setLoading(false);
    }
  };

  const handleAccept = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!inviteData.user_exists) {
      if (!allRequirementsMet) {
        setError('Password does not meet all requirements');
        return;
      }
      if (!passwordsMatch) {
        setError('Passwords do not match');
        return;
      }
    }

    setAccepting(true);
    setError(null);

    try {
      const response = await apiService.post('/auth/invite/accept', {
        token,
        password: inviteData.user_exists ? undefined : password,
        name: inviteData.user_exists ? undefined : name
      });

      localStorage.setItem('accessToken', response.data.accessToken);
      localStorage.setItem('refreshToken', response.data.refreshToken);
      
      navigate('/dashboard');
      
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to accept invitation');
      setAccepting(false);
    }
  };

  // Styles
  const pageStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: 'var(--v2-bg)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px 20px',
    fontFamily: 'var(--font-sans)',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: 'var(--v2-surface)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--v2-border)',
    boxShadow: 'var(--shadow-lg)',
    padding: '40px',
    width: '100%',
    maxWidth: '480px',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: 'var(--v2-text)',
    margin: '0 0 8px 0',
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: '0.9375rem',
    color: 'var(--v2-text-tertiary)',
    margin: '0 0 20px 0',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: 'var(--v2-text)',
    marginBottom: '6px',
  };

  const getInputStyle = (focused: boolean): React.CSSProperties => ({
    width: '100%',
    padding: '10px 12px',
    fontSize: '0.9375rem',
    border: focused ? '1px solid var(--v2-primary)' : '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    backgroundColor: 'var(--v2-surface)',
    color: 'var(--v2-text)',
    outline: 'none',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    boxSizing: 'border-box',
    boxShadow: focused ? '0 0 0 3px var(--v2-primary-muted)' : 'none',
  });

  const getButtonStyle = (hovered: boolean, disabled: boolean): React.CSSProperties => ({
    width: '100%',
    padding: '12px 20px',
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: '#ffffff',
    backgroundColor: disabled ? 'var(--v2-border)' : hovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background-color 0.15s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  });

  const alertStyle = (type: 'error' | 'success' | 'info'): React.CSSProperties => ({
    padding: '12px 16px',
    borderRadius: 'var(--radius-md)',
    borderLeft: `4px solid ${type === 'error' ? 'var(--v2-error)' : type === 'success' ? 'var(--v2-success)' : 'var(--v2-primary)'}`,
    backgroundColor: type === 'error' ? 'var(--v2-error-bg)' : type === 'success' ? 'var(--v2-success-bg)' : 'var(--v2-info-bg)',
    color: type === 'error' ? 'var(--v2-error)' : type === 'success' ? 'var(--v2-success-dark)' : 'var(--v2-primary)',
    fontSize: '0.875rem',
    marginBottom: '20px',
  });

  const infoBoxStyle: React.CSSProperties = {
    padding: '16px',
    backgroundColor: 'var(--v2-surface-alt)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--v2-border)',
    marginBottom: '20px',
  };

  const infoRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '4px 0',
  };


  // Loading state
  if (loading) {
    return (
      <>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={pageStyle}>
          <div style={cardStyle}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 0' }}>
              <Loader2 size={48} strokeWidth={2} color="var(--v2-primary)" style={{ animation: 'spin 1s linear infinite', marginBottom: '16px' }} />
              <p style={{ ...subtitleStyle, marginTop: '16px', marginBottom: 0 }}>Loading invitation...</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Error state (no invite data)
  if (error && !inviteData) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h1 style={titleStyle}>Invalid Invitation</h1>
          <div style={alertStyle('error')}>
            {error}
          </div>
        </div>
      </div>
    );
  }

  // Form state
  const isNewUser = !inviteData?.user_exists;
  const isSubmitDisabled = accepting || (isNewUser && (!name || !allRequirementsMet || !passwordsMatch));

  return (
    <>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={pageStyle}>
        <div style={cardStyle}>
          <h1 style={titleStyle}>Team Invitation</h1>
          <p style={subtitleStyle}>
You've been invited to join a team on {branding?.company_name || 'DashboardFox'}
          </p>

          {error && (
            <div style={alertStyle('error')}>
              {error}
            </div>
          )}

          {/* Invite details */}
          <div style={infoBoxStyle}>
            <div style={infoRowStyle}>
              <span style={{ color: 'var(--v2-text-tertiary)', fontSize: '0.875rem' }}>Email</span>
              <span style={{ color: 'var(--v2-text)', fontWeight: 500, fontSize: '0.875rem' }}>{inviteData?.email}</span>
            </div>
            <div style={infoRowStyle}>
              <span style={{ color: 'var(--v2-text-tertiary)', fontSize: '0.875rem' }}>Role</span>
              <span style={{ 
                color: 'var(--v2-primary)', 
                fontWeight: 500, 
                fontSize: '0.6875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                backgroundColor: 'var(--v2-info-bg)',
                padding: '2px 8px',
                borderRadius: 'var(--radius-sm)',
              }}>
                {inviteData?.role}
              </span>
            </div>
          </div>

          <form onSubmit={handleAccept}>
            {isNewUser ? (
              <>
                <p style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)', marginBottom: '16px' }}>
                  Create your account to join the team:
                </p>

                {/* Name */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onFocus={() => setNameFocused(true)}
                    onBlur={() => setNameFocused(false)}
                    disabled={accepting}
                    autoFocus
                    placeholder="Your full name"
                    style={getInputStyle(nameFocused)}
                  />
                </div>

                {/* Password */}
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setPasswordFocused(true)}
                    onBlur={() => setPasswordFocused(false)}
                    disabled={accepting}
                    autoComplete="new-password"
                    style={getInputStyle(passwordFocused)}
                  />
                </div>

{/* Password requirements */}
                <PasswordRequirements
                  password={password}
                  policy={passwordPolicy}
                  columns={1}
                  style={{ marginBottom: '20px' }}
                />

                {/* Confirm Password */}
                <div style={{ marginBottom: '24px' }}>
                  <label style={labelStyle}>Confirm Password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    onFocus={() => setConfirmFocused(true)}
                    onBlur={() => setConfirmFocused(false)}
                    disabled={accepting}
                    autoComplete="new-password"
                    style={getInputStyle(confirmFocused)}
                  />
                  {confirmPassword && !passwordsMatch && (
                    <p style={{ fontSize: '0.8125rem', color: 'var(--v2-error)', marginTop: '6px', marginBottom: 0 }}>
                      Passwords do not match
                    </p>
                  )}
                  {passwordsMatch && (
                    <p style={{ fontSize: '0.8125rem', color: 'var(--v2-success)', marginTop: '6px', marginBottom: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Check size={14} strokeWidth={2} /> Passwords match
                    </p>
                  )}
                </div>
              </>
            ) : (
              <div style={alertStyle('info')}>
                You already have an account. Click accept to join the team.
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitDisabled}
              onMouseEnter={() => setSubmitHovered(true)}
              onMouseLeave={() => setSubmitHovered(false)}
              style={getButtonStyle(submitHovered, isSubmitDisabled)}
            >
              {accepting ? (
                <>
                  <Loader2 size={18} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
                  <span>Accepting...</span>
                </>
              ) : (
                'Accept Invitation'
              )}
            </button>
          </form>
        </div>
      </div>
    </>
  );
};

export default AcceptInvite;
